import React, { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { ArrowRight, Mail, Phone, Globe, MapPin, Loader2, User, MessageCircle } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { LogoLockup } from "./Logo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const SERVICE_OPTIONS = [
  "Building Materials",
  "Interior Fit-out Supplies",
  "Rider Equipment",
  "Marketing Gifts & Events",
  "Gym & Fitness Equipment",
  "HVAC Spare Parts",
  "ERP Software & Apps",
  "Computer Systems & Software",
  "Commercial Brokerage",
  "Other / General Enquiry",
];

const initial = { name: "", email: "", phone: "", company: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and a brief message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thank you. One Sphere will reach out within one business day.");
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong — please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not submit. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-y bg-background">
      <Toaster position="top-right" richColors closeButton />
      <div className="max-page container-px grid lg:grid-cols-12 gap-12 lg:gap-20">
        <Reveal className="lg:col-span-5">
          <Overline>Contact</Overline>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-foreground">
            Begin a brief.<br />
            <em className="not-italic text-primary/80">We listen first.</em>
          </h2>
          <p className="mt-6 max-w-md text-muted-foreground leading-relaxed">
            Share a few details about your project or procurement need.
            A category lead — not a chatbot — will respond personally.
          </p>

          {/* Trust strip — conversion lifters */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="contact-trust-strip">
            {[
              { k: "1 business day", v: "Average response time" },
              { k: "Direct line", v: "Speak to a category lead" },
              { k: "No middlemen", v: "Source-direct quotations" },
              { k: "Confidential", v: "Your details are never shared" },
            ].map((t) => (
              <div key={t.k} className="flex items-start gap-3 p-3 rounded-xl bg-muted/50 border border-border/60">
                <span className="mt-1 w-2 h-2 rounded-full bg-primary shrink-0" />
                <div>
                  <div className="text-sm font-medium text-foreground leading-tight">{t.k}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{t.v}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 space-y-6">
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full border border-border flex items-center justify-center">
                <User className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">General Manager</div>
                <div className="font-medium text-foreground">Mohammed Muzakir</div>
              </div>
            </div>

            <a
              href="https://wa.me/971555752118?text=Hi%20One%20Sphere%2C%20I%27d%20like%20to%20enquire%20about%20your%20services."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 rounded-full border border-border flex items-center justify-center group-hover:bg-[#25D366] group-hover:text-white group-hover:border-[#25D366] transition-all duration-500">
                <MessageCircle className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">WhatsApp</div>
                <div className="font-medium text-foreground group-hover:text-[#1da851] transition-colors">
                  Chat with us instantly
                </div>
              </div>
            </a>

            <a
              href="tel:+971555752118"
              data-testid="contact-phone"
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 rounded-full border border-border flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all duration-500">
                <Phone className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Phone</div>
                <div className="font-medium text-foreground group-hover:text-primary transition-colors">
                  055 575 2118
                </div>
              </div>
            </a>

            <a
              href="mailto:muzakir@one-sphere.com"
              data-testid="contact-email"
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 rounded-full border border-border flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all duration-500">
                <Mail className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Email</div>
                <div className="font-medium text-foreground group-hover:text-primary transition-colors">
                  muzakir@one-sphere.com
                </div>
              </div>
            </a>

            <a
              href="https://www.one-sphere.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-web"
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 rounded-full border border-border flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all duration-500">
                <Globe className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Web</div>
                <div className="font-medium text-foreground group-hover:text-primary transition-colors">
                  www.one-sphere.com
                </div>
              </div>
            </a>

            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full border border-border flex items-center justify-center">
                <MapPin className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Office</div>
                <div className="font-medium text-foreground">United Arab Emirates</div>
              </div>
            </div>
          </div>

          <div className="mt-14 hidden lg:block">
            <LogoLockup height={42} />
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="p-8 sm:p-10 lg:p-12 rounded-2xl border border-border bg-card"
          >
            <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
              <Field label="Full Name *" testid="field-name">
                <input
                  required
                  data-testid="input-name"
                  value={form.name}
                  onChange={update("name")}
                  className="w-full bg-transparent border-0 border-b border-border py-3 focus:border-primary outline-none transition-colors text-foreground"
                  placeholder="Your name"
                />
              </Field>
              <Field label="Email *" testid="field-email">
                <input
                  required
                  type="email"
                  data-testid="input-email"
                  value={form.email}
                  onChange={update("email")}
                  className="w-full bg-transparent border-0 border-b border-border py-3 focus:border-primary outline-none transition-colors text-foreground"
                  placeholder="you@company.com"
                />
              </Field>
              <Field label="Phone" testid="field-phone">
                <input
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  className="w-full bg-transparent border-0 border-b border-border py-3 focus:border-primary outline-none transition-colors text-foreground"
                  placeholder="Optional"
                />
              </Field>
              <Field label="Company" testid="field-company">
                <input
                  data-testid="input-company"
                  value={form.company}
                  onChange={update("company")}
                  className="w-full bg-transparent border-0 border-b border-border py-3 focus:border-primary outline-none transition-colors text-foreground"
                  placeholder="Company name"
                />
              </Field>
              <Field label="Service of Interest" testid="field-service" className="sm:col-span-2">
                <select
                  data-testid="input-service"
                  value={form.service}
                  onChange={update("service")}
                  className="w-full bg-transparent border-0 border-b border-border py-3 focus:border-primary outline-none transition-colors text-foreground appearance-none cursor-pointer"
                >
                  <option value="">Select a category</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label="Brief / Message *" testid="field-message" className="sm:col-span-2">
                <textarea
                  required
                  data-testid="input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  className="w-full bg-transparent border-0 border-b border-border py-3 focus:border-primary outline-none transition-colors text-foreground resize-none"
                  placeholder="Tell us about your project, quantities, timeline or question…"
                />
              </Field>
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-between gap-6">
              <p className="text-xs text-muted-foreground max-w-sm">
                By submitting this form you consent to be contacted by One Sphere
                regarding your enquiry. We do not share your details with third parties.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="submit-lead-button"
                className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 rounded-full text-sm tracking-wide hover:bg-foreground transition-all duration-500 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send Enquiry
                    <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

const Field = ({ label, children, className = "", testid }) => (
  <label className={`block ${className}`} data-testid={testid}>
    <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
